import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { FlightInfo, Pax } from 'ts/dynamic/interfaces/DynamicBookResponse';
import { useTranslation } from 'react-i18next';
import {
  dynAgesList,
  genderOptions,
  getBookingAges,
  getBookingAgesStr,
  PassengerType,
} from 'constants/dynamic';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import { Select } from 'ts/interfaces/Generic';
import { getDynPassengerValidation } from 'utils/dynamic/dynamic';
import { dateFormat } from 'utils/shared/utils';
import PhoneInput from 'components/shared/ui/PhoneInput';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { addYears } from 'utils/utils';
import { getFlightAirlines } from 'services/dynamic';
import { BookOptionsCatalogue } from 'ts/dynamic/classes/BookOptionsCatalogue';
import OptionalServices from '../Utils/OptionalServices';
import { useLocation } from 'react-router-dom';
import { b2b } from 'constants/generic';

interface DynamicFlightFormProps {
  paxes: Pax[];
  flight: FlightInfo;
  isSubmitted: boolean;
  setPaxes: (paxes: Pax[]) => void;
  addPrice: number;
  onUpdateAddPrice: (addPrice: number) => void;
  currency: string;
}

const DynamicFlightForm: FC<DynamicFlightFormProps> = ({ isSubmitted, ...props }) => {
  const [airlines, setAirlines] = useState<Select[]>([]);
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const catalogue = useRef(
    new BookOptionsCatalogue(
      props.flight.bookFareAndBaggage.bookFareAndBaggageFlights as any,
      props.currency?.toLowerCase() || 'usd'
    )
  );

  useEffect(() => {
    if (b2b && pathname !== '/dynamic/book') {
      return;
    }
    (async () => {
      const airlines = await getFlightAirlines();
      setAirlines(airlines);
    })();
  }, []); // eslint-disable-line

  const onChangeField = (newPax: Pax, index: number) => {
    const newPaxes = [...props.paxes];
    newPaxes[index] = newPax;

    newPaxes.forEach((pax) => {
      pax.baggageOutbound = [
        ...(pax.baggageOutboundArr || []),
        ...(pax.mealOutboundArr || []),
      ].join(',');
      pax.baggageInbound = [...(pax.baggageInboundArr || []), ...(pax.mealInboundArr || [])].join(
        ','
      );
    });
    props.setPaxes(newPaxes);

    const addPrice = newPaxes.reduce((acc, pax) => {
      const bagArr = [...(pax.baggageOutboundArr || []), ...(pax.mealOutboundArr || [])];
      const bagCatalogue = [
        ...catalogue.current.outboundBaggage.options,
        ...catalogue.current.outboundMeal.options,
      ];

      const paxPrice = bagArr.reduce((acc, bagId) => {
        const catal = bagCatalogue.find((cat) => cat.value === bagId);
        return acc + (catal?.data.price || 0);
      }, 0);

      const bagArrIn = [...(pax.baggageInboundArr || []), ...(pax.mealInboundArr || [])];
      const bagCatalogueIn = [
        ...catalogue.current.inboundBaggage.options,
        ...catalogue.current.inboundMeal.options,
      ];

      const paxPriceIn = bagArrIn.reduce((acc, bagId) => {
        const catal = bagCatalogueIn.find((cat) => cat.value === bagId);
        return acc + (catal?.data.price || 0);
      }, 0);

      return acc + paxPrice + paxPriceIn;
    }, 0);

    if (props.addPrice !== addPrice) {
      props.onUpdateAddPrice(addPrice);
    }
  };

  const isOptionsVisible = (paxIndex: number) => {
    const someVisible = catalogue.current.visible;
    const includesPaxType = [
      ...catalogue.current.outboundBaggage.paxTypes,
      ...catalogue.current.outboundMeal.paxTypes,
      ...catalogue.current.inboundBaggage.paxTypes,
      ...catalogue.current.inboundMeal.paxTypes,
    ].includes(props.paxes[paxIndex].paxTypeCode);
    return someVisible && includesPaxType;
  };

  return (
    <div>
      {props.paxes.map((pax, index) => (
        <div className="book-form-room" key={index}>
          <div className="title text-md mb-15">
            <span className="mr-5" style={{ display: 'inline-flex' }}>
              {t('passenger.passenger')} {index + 1}
            </span>

            <span className="text-sm text-light text-normal">
              {getBookingAgesStr(pax.paxTypeCode, dynAgesList)}
            </span>
          </div>
          <div className="form-wrapper row">
            {pax.paxTypeCode === PassengerType.Child || pax.paxTypeCode === PassengerType.Infant ? (
              <FormGroup
                disabled={true}
                onChange={() => {}}
                value={t('child')}
                label={t('passenger.gender')}
                className="col-md-3 col-12"
              />
            ) : (
              <FormGroupSelect
                onChange={(gender) => onChangeField({ ...pax, gender }, index)}
                value={pax.gender}
                label={t('passenger.gender')}
                className="col-md-3 col-12"
                options={genderOptions}
                needToTranslate={true}
              />
            )}

            <FormGroup
              name="firstName"
              onChange={(firstName) => onChangeField({ ...pax, firstName }, index)}
              value={pax.firstName}
              label={t('passenger.fn')}
              className="col-md-3 col-12"
              validation={getDynPassengerValidation(index).firstName}
              isSubmitted={isSubmitted}
            />
            <FormGroup
              name="lastName"
              onChange={(lastName) => onChangeField({ ...pax, lastName }, index)}
              value={pax.lastName}
              label={t('passenger.ln')}
              className="col-md-3 col-12"
              validation={getDynPassengerValidation(index).lastName}
              isSubmitted={isSubmitted}
            />
            <FormGroupDatepicker
              label={t('passenger.dob')}
              onChange={(dob) =>
                onChangeField({ ...pax, dob: dateFormat(dob, 'yyyy-MM-dd') }, index)
              }
              value={
                pax.dob
                  ? new Date(pax.dob)
                  : addYears(
                      -getBookingAges(pax.paxTypeCode, dynAgesList).min,
                      new Date(props.flight.flight.outbound.departureDateTime)
                    )
              }
              className="col-md-3 col-12"
              validation={getDynPassengerValidation(index).dob}
              isSubmitted={isSubmitted}
              minDate={addYears(
                -getBookingAges(pax.paxTypeCode, dynAgesList).max,
                new Date(props.flight.flight.outbound.departureDateTime)
              )}
              maxDate={addYears(
                -getBookingAges(pax.paxTypeCode, dynAgesList).min,
                new Date(props.flight.flight.outbound.departureDateTime)
              )}
            />
          </div>
          <div className="form-wrapper row">
            {index === 0 && (
              <Fragment>
                <PhoneInput
                  onChange={(phone) => onChangeField({ ...pax, phone }, index)}
                  value={pax.phone}
                  isSubmitted={isSubmitted}
                  validation={getDynPassengerValidation(index).phone}
                  className="col-md-6 col-12"
                />
                <FormGroup
                  name="email"
                  onChange={(email) => onChangeField({ ...pax, email }, index)}
                  value={pax.email}
                  label={t('passenger.email')}
                  className="col-md-6 col-12"
                  validation={getDynPassengerValidation(index).email}
                  isSubmitted={isSubmitted}
                />
              </Fragment>
            )}

            <FormGroupSelect
              onChange={(flightFF_AirlineCode) =>
                onChangeField({ ...pax, flightFF_AirlineCode }, index)
              }
              value={pax.flightFF_AirlineCode}
              label={t('flight.frequent_flyer_airline')}
              options={airlines}
              className="col-md-6 col-12"
            />
            <FormGroup
              name="flightFF_Number"
              onChange={(flightFF_Number) => onChangeField({ ...pax, flightFF_Number }, index)}
              value={pax.flightFF_Number}
              label={t('flight.frequent_flyer_number')}
              className="col-md-6 col-12"
            />
          </div>
          {isOptionsVisible(index) && (
            <div className="book-optional">
              <div className="title">{t('passenger.optional_services')}</div>
              <OptionalServices
                pax={pax}
                paxIndex={index}
                onChangeField={onChangeField}
                flightSource={props.flight.flight.flightSource}
                catalogue={catalogue.current}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default withErrorBoundary(DynamicFlightForm, 'DynamicFlightForm');
